import { defaultPageSize } from '@bioliberty/core';
import { CirclePlus, Search } from 'lucide-react';
import { useState } from 'react';
import { useGetPatientsQuery } from '../../api';
import { Button } from '../../components/Button';
import { Heading } from '../../components/Heading';
import { TextInput } from '../../components/Input/TextInput';
import { useDebounce } from '../../hooks/useDebounce';
import { useTenantIds } from '../../hooks/useTenantIds';
import { usePatientViewDataTableState } from '../hooks/usePatientViewDataTableState';
import { AddPatientModal } from './AddPatientModal';
import { PatientDataTable } from './PatientDataTable';

export const PatientsList = () => {
  const tenantIds = useTenantIds();

  const [defaultState] = useState({
    page: 1,
    pageSize: 15,
    sorting: [
      {
        id: 'lastActivityDate',
        desc: true
      }
    ],
    searchTerm: ''
  });

  const [{ page, sorting, searchTerm }, setDataTableSorting, setDataTablePage, setDataTableSearchTerm] =
    usePatientViewDataTableState(defaultState, 'patientList');

  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [searchInput, setSearchInput] = useState(searchTerm || '');
  useDebounce(searchInput, 600, setDataTableSearchTerm);

  const { data: queryResult, isLoading } = useGetPatientsQuery({
    tenantIds,
    searchTerm: searchTerm || undefined,
    page: page || 1,
    pageSize: defaultState.pageSize,
    sortBy: sorting?.[0]?.id,
    sortOrder: sorting?.[0] ? (sorting[0].desc ? 'DESC' : 'ASC') : undefined
  });

  const totalPages = Math.ceil((queryResult?.totalPatients || 0) / defaultPageSize);

  return (
    <div className="flex-1 flex-col justify-between">
      <AddPatientModal open={showAddPatientModal} onOpenChange={setShowAddPatientModal} />
      <div className="flex w-full items-center justify-center md:justify-between">
        <Heading size="l" className="hidden md:block">
          Patients
        </Heading>
        <div className="flex h-[54px] flex-row items-center gap-5 md:pl-5">
          <TextInput
            icon={Search}
            initialValue={searchInput}
            placeholder="Search by ID"
            onChange={setSearchInput}
            size="l"
            autoFocus
            testId="patients-search-input"
          />
          <Button
            icon={CirclePlus}
            iconLayout="start"
            onClick={() => {
              setShowAddPatientModal(true);
            }}
          >
            <div className="hidden md:block">Add Patient</div>
          </Button>
        </div>
      </div>
      <div className="flex max-h-full flex-col overflow-auto">
        <PatientDataTable
          patients={queryResult?.patients || []}
          loading={isLoading}
          onSortingChange={setDataTableSorting}
          onPaginationChange={(state) => setDataTablePage((state?.pageIndex ?? 0) + 1)}
          initialSorting={sorting}
          pageCount={totalPages}
          pageSize={defaultState.pageSize}
        />
      </div>
    </div>
  );
};
